import { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';

import notification from 'epn-ui/umd/dist/components/Notification';
import Grid from 'epn-ui/umd/dist/components/Grid';
import Loader from 'epn-ui/umd/dist/components/Loader';
import FormMarking from '@components/pages/tools/marking/form';

import AffiliateCopyable from '@pages/tools/affiliate-links/AffiliateCopyable';

import useI18n from '@i18n';

import api from '@api';
import apiHooks from '@apiHooks';

// TYPES
import type {
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
    IPostLinkReductionDataFormatted,
    IResponse,
    IGetDomainCuttersData,
    IGetCreativesData,
} from '@api/types';
import { isGlobalDomain } from '@config/globalDomain';
import type { IForm, ICreativeState } from './interfaces';

// HELPERS
import { prepareCreativeData, prepareFormFields, sendFormData } from './helpers';

// Config
// eslint-disable-next-line import/order
import {
    OFFERS_PARAMS,
    PLACEMENTS_PARAMS,
    INITIAL_CREATIVE,
} from '@components/pages/tools/marking/config';

const { useOffers, usePlacements, useReduceLinks } = apiHooks;

const MarkingPageForm: React.FC = () => {
    const { t, locale } = useI18n();
    const [offerId, setOfferId] = useState<number | null>(null);
    const [reductionListLoading, setReductionListLoading] = useState<boolean>(true);
    const [reductionListError, setReductionListError] = useState<string | unknown>();
    const [reductionList, setReductionList] = useState<
        {
            label: string;
            value: string;
        }[]
    >(null);
    const [reductionType, setReductionType] = useState<string | null>(null);
    const [reductionsNeed, setReductionsNeed] = useState<boolean>(false);
    const [creativeIsLoading, setCreativeIsLoading] = useState<boolean>(false);
    const [creative, setCreative] = useState<ICreativeState>(INITIAL_CREATIVE);
    const [formError, setFormError] = useState(null);

    // Get Reductions
    const getReductions = async (): Promise<void> => {
        try {
            const res: IResponse<IGetDomainCuttersData> = await api.linkReduction.getDomainCutterList(
                {},
            );
            if (res.result && res.data?.attributes) {
                const preparedReduction = res.data?.attributes.map(item => ({
                    value: item,
                    label: item,
                }));
                setReductionList(preparedReduction);
                setReductionType(preparedReduction[0].value);
                setReductionListLoading(false);
            }
        } catch (err: unknown) {
            setReductionListError(err);
            console.error(err);
            notification.error({ message: t('Something went wrong') });
        }
    };

    useEffect(() => {
        getReductions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get offers
    const onGetOffers = data => {
        setOfferId(data[0][0]?.id);
    };
    const { data: offersData, isLoading: offersIsLoading, error: offersError } = useOffers<
        IGetOffersDataFormatted[]
    >({
        params: {
            ...OFFERS_PARAMS,
            lang: locale,
        },
        dependencies: [locale],
        options: { onSuccess: onGetOffers },
    });

    // Get Placements
    const {
        data: placementsData = null,
        isLoading: placementsIsLoading,
        error: placementsError,
    } = usePlacements<IGetPlacementsDataFormatted[]>({
        params: { ...PLACEMENTS_PARAMS, offerIds: offerId },
        dependencies: [offerId],
        condition: !!offerId,
    });

    const {
        data: reduceLinks = [],
        error: reduceLinkError = false,
        isLoading: reduceLoading = false,
    } = useReduceLinks<IPostLinkReductionDataFormatted[]>({
        params: {
            urlContainer: [{ url: creative?.code, domainCutter: reductionType }],
        },
        dependencies: [reductionType],
        condition: creative?.code?.length > 0 && !!reductionsNeed,
    });

    // Отображение ошибок
    useEffect(() => {
        if (offersError || reduceLinkError || placementsError || reductionListError) {
            let msg;
            if (offersError) {
                msg = t('An error occurred while fetching the list of offers');
            }
            if (reduceLinkError) {
                msg = t('An error occurred while shortening the link');
            }
            if (placementsError) {
                msg = t('An error occurred while fetching the list of placements');
            }
            if (reductionListError) {
                msg = t('An error occurred while fetching the list of link shorteners');
            }
            notification.error({ message: t(msg) });
            console.error(offersError || reduceLinkError || placementsError || reductionListError);
        }
    }, [offersError, reduceLinkError, placementsError, reductionListError, t]);

    const resetForm = useCallback((): void => {
        setFormError(null);
        onGetOffers([offersData]);
    }, [offersData]);

    const onSubmitForm = useCallback(
        async (form: IForm) => {
            setCreativeIsLoading(true);
            setFormError(null);
            // Вытаскиваю только нужные поля для креатива
            const preparedForm = prepareFormFields(form, t('New link'));

            try {
                const response: IResponse<IGetCreativesData> =
                    preparedForm.ordForm !== 'text'
                        ? await sendFormData(preparedForm)
                        : await api.creative.createCreative({
                              body: preparedForm,
                          });
                if (response?.result) {
                    setCreative(prepareCreativeData(response, form, t));
                    notification.success({ message: t('Creative create success') });
                    resetForm();
                    setCreativeIsLoading(false);
                }
                if (!response?.result && response.errors.length > 0) {
                    setFormError(response.errors);
                }

                return response;
            } catch (error) {
                return error;
            } finally {
                setCreativeIsLoading(false);
            }
        },
        [resetForm, t],
    );

    const partnerLinkUrl = reductionsNeed ? reduceLinks[0]?.link : creative.code;
    const showCopyInputs =
        !!partnerLinkUrl?.length || !!creative?.erid.length || !!creative?.ordFullData.length;

    return (
        <div className="mb-36">
            <Head>
                <title>{t('Marking')}</title>
            </Head>
            <div className="mb-8">
                <h2 className="font-medium text-blueGray-600 text-sub xs:text-h2">
                    {t('Marking')}
                </h2>
                <p className="mt-4 text-p2 text-blueGray-500">
                    {t('Mark your advertising creatives before publication...')}{' '}
                    <br className="lg:hidden" />
                    <a
                        href="https://drive.google.com/file/d/13Lsux-gcJnLQzb-X8Wqg0dDe3Q8gNJyl/view?usp=sharing"
                        target="__blank"
                        className="underline text-blueGray-800"
                    >
                        {t('Instruction')}
                    </a>
                </p>
            </div>
            <Loader loading={creativeIsLoading}>
                <FormMarking
                    offersIsLoading={offersIsLoading}
                    placementsIsLoading={placementsIsLoading}
                    offers={offersData}
                    onChangeOffer={setOfferId}
                    placements={placementsData}
                    reductionList={reductionList}
                    reductionListLoading={reductionListLoading}
                    onChangeReduction={setReductionType}
                    reductionType={reductionType}
                    reductionsNeed={reductionsNeed}
                    setReductionsNeed={setReductionsNeed}
                    onSubmitForm={onSubmitForm}
                    formError={formError}
                    offerId={offerId}
                    isLoading={creativeIsLoading}
                />
                {showCopyInputs && (
                    <Grid className="my-8" gutter={[32, 24]}>
                        <Grid.Col xs={24} xl={24}>
                            <Loader loading={reduceLoading}>
                                <AffiliateCopyable
                                    label={t('Your affiliate link')}
                                    text={partnerLinkUrl}
                                    error={
                                        reduceLinkError
                                            ? t('Error while generating affiliate link')
                                            : null
                                    }
                                />
                            </Loader>
                        </Grid.Col>
                        <Grid.Col xs={24} xl={12}>
                            <AffiliateCopyable label={t('Token')} text={creative?.erid} />
                        </Grid.Col>
                        <Grid.Col xs={24} xl={12}>
                            <AffiliateCopyable
                                label={t('Advertiser')}
                                text={creative?.ordFullData}
                                description={
                                    isGlobalDomain
                                        ? 'This information is needed for advertising labeling.'
                                        : t('This information is needed for advertising labeling.')
                                }
                            />
                        </Grid.Col>
                    </Grid>
                )}
            </Loader>
        </div>
    );
};

export default MarkingPageForm;
